import type { RefreshOutcome } from './syncBlob';
import { syncManager } from './syncManager';

/**
 * ⭐ A tab that comes back SHOWS — and uses — what the cloud holds now.
 *
 * The per-user blob docs (identity, coach, AI chat, schedules, …) load once per
 * page. Their writes are safe on a stale tab (syncDoc's three-way merge), but
 * the tab kept DISPLAYING and USING the copy it loaded until it wrote or
 * reloaded. The sharp case is privacy: AI visibility lives in the identity doc,
 * and the chat filters what it sends by this tab's copy — so a source refused
 * on the phone was still sent from a laptop tab opened before the refusal.
 *
 * When the page becomes visible again (or the window gets focus), every
 * registered store catches up through its sync's `refresh` (lib/syncBlob.ts):
 * on the store's write chain, it asks for the row's `updated_at` alone, and
 * only when that moved does it read the row and fold it in — unsaved edits here
 * are kept and written, everything else becomes the cloud's.
 *
 * ⚠️ EGRESS (see lib/cloudBoot.ts). No polling: only a tab coming into view
 * triggers it, at most once per REFRESH_AFTER_MS per store, and a store whose
 * row has not moved costs one timestamp, not its blob.
 */

export const REFRESH_AFTER_MS = 60_000;

interface Registered {
  refresh: () => Promise<RefreshOutcome>;
  /** When the last refresh started; null = never. */
  at: number | null;
}

const registered = new Map<string, Registered>();

/**
 * Register a store's catch-up under `key` (its table). Re-registering a key
 * replaces it — a hot-reloaded module must not refresh twice.
 */
export function refreshBlobOnFocus(key: string, refresh: () => Promise<RefreshOutcome>): void {
  registered.set(key, { refresh, at: registered.get(key)?.at ?? null });
}

/**
 * The usual `ready` for a refresh: the store has loaded, and for the account
 * signed in NOW — a refresh must never fold another account's row into it.
 */
export function loadedFor(initializedUserId: () => string | null): () => Promise<boolean> {
  return async () => {
    const { user } = await syncManager.safeAuthCheck();
    return !!user && initializedUserId() === user.id;
  };
}

/** Refresh every registered store that is due. Resolves when they have all answered. */
export async function refreshDueBlobs(): Promise<void> {
  const now = Date.now();
  const due = [...registered.values()].filter(e => e.at === null || now - e.at >= REFRESH_AFTER_MS);
  await Promise.all(due.map(async (e) => {
    const before = e.at;
    // Counted from now: `visibilitychange` and `focus` usually fire together.
    e.at = now;
    const outcome = await e.refresh().catch((err: unknown) => {
      console.error('Error refreshing a synced store:', err);
      return 'failed' as const;
    });
    // A store that had nothing to refresh (not loaded yet, signed out) is due
    // again the moment it is — it has not spent its turn.
    if (outcome === 'skipped') e.at = before;
  }));
}

/** Tests: every store due again (`forget`: and none registered). */
export function __resetBlobFocusRefresh(opts: { forget?: boolean } = {}): void {
  if (opts.forget) registered.clear();
  for (const e of registered.values()) e.at = null;
}

let stop: (() => void) | null = null;

/** Start listening (main.tsx). Idempotent; returns the stop. */
export function startBlobFocusRefresh(): () => void {
  if (stop) return stop;
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};
  const onVisible = () => { if (document.visibilityState === 'visible') void refreshDueBlobs(); };
  const onFocus = () => { void refreshDueBlobs(); };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onFocus);
  stop = () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onFocus);
    stop = null;
  };
  return stop;
}
